import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { UserCheck, CheckCircle, Users, TrendingUp, Building, Heart } from 'lucide-react';

const HRProfessionals = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What should HR professionals include in their performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Include hiring metrics (time-to-fill, quality of hire), employee engagement scores, turnover reduction, training program effectiveness, compliance achievements, and cost savings from HR initiatives. Quantify impact: 'Reduced time-to-fill from 45 to 28 days while improving quality of hire scores 23%.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do HR professionals demonstrate strategic value?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document business outcomes tied to HR initiatives: reduced turnover costs, improved productivity from engagement programs, revenue enablement through hiring, and risk mitigation through compliance. Show how HR work drives business results, not just HR metrics."
        }
      },
      {
        "@type": "Question",
        "name": "What metrics matter for HR performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key metrics include: time-to-fill, cost-per-hire, employee engagement scores, voluntary turnover rate, training completion rates, internal mobility rates, and compliance audit results. Also track employee satisfaction with HR services and manager feedback."
        }
      },
      {
        "@type": "Question",
        "name": "How often should HR track their accomplishments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Track monthly at minimum, capturing hiring wins, employee relations successes, and program launches as they happen. Document feedback from managers and employees immediately. Use a tool like BragBox to capture achievements in real-time."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for HR Professionals | BragBox</title>
        <meta name="description" content="Ace your HR performance review with actionable tips. Learn how to showcase hiring impact, employee engagement wins, and strategic business contributions." />
        <meta name="keywords" content="HR performance review, human resources self-evaluation, HR metrics, talent acquisition review, HR career growth, SHRM" />
        <link rel="canonical" href="https://bragbox.ai/tips/hr-professionals" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <UserCheck className="w-4 h-4" />
              Human Resources Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for HR Professionals
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              You're the people behind the people strategy. Learn how to document hiring wins, culture initiatives, and the strategic impact that drives business results.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why HR Struggles with Self-Evaluations</h2>
            <p className="text-gray-300 mb-6">
              You hired the engineering team that shipped the company's biggest feature. You navigated a complex employee relations issue that could have become a lawsuit. You designed the engagement program that reduced turnover by 20%. But when review time comes, leadership still asks "How many positions did you fill?"
            </p>
            <p className="text-gray-300">
              HR work is often invisible precisely because it enables others to succeed. The hire who becomes a star performer? Credit goes to their manager. The conflict you de-escalated? Nobody knows it almost went sideways. Your best work often has no direct attribution to you.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your HR Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Quantify Hiring Impact</h3>
                    <p className="text-gray-300 mb-3">
                      Go beyond headcount. Show time-to-fill improvements, quality of hire metrics, and the business outcomes your hires enabled.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Filled 34 positions with 28-day average time-to-fill (down from 45 days). 90-day retention rate of 94%. New hires in engineering team shipped product 2 weeks ahead of schedule, generating $180K in accelerated revenue."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Turnover Reduction</h3>
                    <p className="text-gray-300 mb-3">
                      Every retained employee is cost avoided. Calculate turnover cost savings and attribute them to specific HR initiatives.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Reduced voluntary turnover from 18% to 12% through stay interview program and manager training. At $45K average replacement cost, this represents $270K in avoided costs for 6 retained employees."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Track Employee Engagement Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Engagement surveys tell a story. Document score improvements, action items completed, and initiatives launched based on feedback.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Improved overall engagement score from 68 to 79 (16% increase). Addressed top 3 concerns from prior survey, with satisfaction on those items increasing 34%. Participation rate reached 89%, highest in company history."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Highlight Compliance Achievements</h3>
                    <p className="text-gray-300 mb-3">
                      Clean audits and avoided penalties are real value. Document compliance maintenance, policy updates, and risk mitigation.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved clean I-9 audit with zero findings across 450 employees. Updated 12 policies for new state law compliance, avoiding potential $180K in penalties. Completed mandatory harassment training at 100% completion rate."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Training Program Impact</h3>
                    <p className="text-gray-300 mb-3">
                      Training develops capabilities. Track completion rates, skill improvements, and business outcomes from learning initiatives.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Launched management training program with 45 managers completing 6-module curriculum. Post-training survey showed 40% improvement in manager confidence. Teams with trained managers saw 15% higher engagement scores."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Track Employee Relations Wins</h3>
                    <p className="text-gray-300 mb-3">
                      Successfully resolved ER issues prevent costly outcomes. Document investigations completed, conflicts de-escalated, and outcomes achieved.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Resolved 23 employee relations cases with zero escalation to external agencies. Implemented early intervention process reducing formal complaints by 35%. Mediated 4 manager-employee conflicts with all parties remaining employed."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Document Process Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Streamlined HR processes save time for everyone. Track automation, workflow improvements, and efficiency gains.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Implemented automated onboarding workflow reducing admin time by 6 hours per new hire. Digitized benefits enrollment cutting enrollment errors by 80%. Self-service portal reduced routine HR inquiries by 45%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Highlight Diversity and Inclusion Progress</h3>
                    <p className="text-gray-300 mb-3">
                      D&I initiatives drive innovation and engagement. Track representation improvements, program launches, and culture metrics.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Improved diverse candidate pipeline from 25% to 42% through sourcing strategy changes. Launched 3 ERGs with 120 active members. Inclusion index on engagement survey increased 18 points year-over-year."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Capture Business Partnership Wins</h3>
                    <p className="text-gray-300 mb-3">
                      Strategic HR is about business enablement. Document how you've partnered with leaders to solve business problems through people strategies.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Partnered with Sales leadership on restructure that improved quota attainment 23%. Designed retention package for critical engineering talent, retaining 5 key employees during competitive market. Advised executives on 3 sensitive personnel decisions."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Track Wins Continuously</h3>
                    <p className="text-gray-300 mb-3">
                      HR work happens daily. Document hiring wins, program launches, and positive feedback as they happen.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After every successful hire, resolved ER case, or positive manager feedback, take 30 seconds to log it in BragBox. Include metrics while they're fresh. When review season arrives, you'll have a year of achievements ready to present.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics HR Professionals Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Talent Acquisition</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Time-to-fill</li>
                  <li>• Cost-per-hire</li>
                  <li>• Quality of hire metrics</li>
                  <li>• Offer acceptance rate</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Engagement & Retention</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Employee engagement scores</li>
                  <li>• Voluntary turnover rate</li>
                  <li>• 90-day retention rate</li>
                  <li>• Internal mobility rate</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Development & Training</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Training completion rates</li>
                  <li>• Promotion rates</li>
                  <li>• Performance rating distribution</li>
                  <li>• Skill gap closure</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Operations & Compliance</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Compliance audit results</li>
                  <li>• HR service satisfaction</li>
                  <li>• Process efficiency gains</li>
                  <li>• ER case resolution rates</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should HR professionals include in their performance review?</h3>
                <p className="text-gray-300">Include hiring metrics (time-to-fill, quality of hire), employee engagement scores, turnover reduction, training program effectiveness, compliance achievements, and cost savings from HR initiatives. Quantify impact: "Reduced time-to-fill from 45 to 28 days while improving quality of hire scores 23%."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do HR professionals demonstrate strategic value?</h3>
                <p className="text-gray-300">Document business outcomes tied to HR initiatives: reduced turnover costs, improved productivity from engagement programs, revenue enablement through hiring, and risk mitigation through compliance. Show how HR work drives business results, not just HR metrics.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics matter for HR performance reviews?</h3>
                <p className="text-gray-300">Key metrics include: time-to-fill, cost-per-hire, employee engagement scores, voluntary turnover rate, training completion rates, internal mobility rates, and compliance audit results. Also track employee satisfaction with HR services and manager feedback.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should HR track their accomplishments?</h3>
                <p className="text-gray-300">Track monthly at minimum, capturing hiring wins, employee relations successes, and program launches as they happen. Document feedback from managers and employees immediately. Use a tool like BragBox to capture achievements in real-time.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Your People Work Deserves Recognition</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps HR professionals capture hiring wins, engagement successes, and strategic contributions throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HRProfessionals;
